import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { CartUploadAnimation } from '@ff-os/shared-react/components/loaders/upload-arrows/CartUploadAnimation';
import { CloudUploadAnimation } from '@ff-os/shared-react/components/loaders/upload-arrows/CloudUploadAnimation';
import { WindowBody } from '@ff-os/shared-react/components/window/partials/WindowBody';
import { WindowFooter } from '@ff-os/shared-react/components/window/partials/WindowFooter';
import { WindowHeader } from '@ff-os/shared-react/components/window/partials/WindowHeader';
import { Window } from '@ff-os/shared-react/components/window/Window';
import { noop } from 'lodash';
import styles from './AddingToCartWindow.module.scss';

export const AddingToCartWindow = ({
    amountOfUploadsRemaining, close, isAddingToCart,
    hasError, dismissError
}: Props) => {

    const { t } = useTranslation();

    const closeError = () => {
        dismissError();
        close();
    };

    if (hasError) {
        return (
            <Window className={styles.errorWindow} close={closeError} size='sm' mobileOrigin='center'>
                <WindowHeader onClose={closeError}>
                    {t('add_to_cart.error.title')}
                </WindowHeader>
                <WindowBody>
                    <Alert type='error' icon='alert-circle-exclamation'>
                        {t('add_to_cart.error.message')}
                    </Alert>
                </WindowBody>
                <WindowFooter>
                    <Button className={styles.closeButton} style='depressed' onClick={closeError}>
                        {t('commons.close')}
                    </Button>
                </WindowFooter>
            </Window>
        );
    } else if (amountOfUploadsRemaining > 0) {
        return (
            <Window close={close} size='sm' mobileOrigin='center'>
                <div className={styles.window}>
                    <WindowHeader className={styles.header} onClose={close} />
                    <WindowBody>
                        <div className={styles.content}>
                            <CloudUploadAnimation className={styles.icon} />
                            <h3 className={styles.title}>
                                {t('upload_progress.amount_of_uploads_remaining', {
                                    count: amountOfUploadsRemaining
                                })}
                            </h3>
                            <p className={styles.subtitle}>{t('photo_prints.uploading_subtitle')}</p>
                        </div>
                    </WindowBody>
                </div>
            </Window>
        );
    } else if (isAddingToCart) {
        return (
            <Window close={noop} size='sm' mobileOrigin='center'>
                <WindowBody>
                    <div className={styles.content}>
                        <CartUploadAnimation className={styles.icon} />
                        <h3 className={styles.title}>
                            {t('photo_prints.adding_to_cart')}
                        </h3>
                        <p className={styles.subtitle}>{t('photo_prints.adding_to_cart_subtitle')}</p>
                    </div>
                </WindowBody>
            </Window>
        );
    } else {
        return null;
    }
};

type Props = {
    dismissError: () => void;
    hasError: boolean;
    close: () => void;
    isAddingToCart: boolean;
    amountOfUploadsRemaining: number;
};